import { useState, useRef, useEffect } from 'react';

interface FloatingVolumeControlProps {
  volume: number; // 0.0 to 1.0
  onVolumeChange: (volume: number) => void;
  onMuteToggle: () => void;
  isMuted?: boolean;
}

export function FloatingVolumeControl({
  volume,
  onVolumeChange,
  onMuteToggle,
  isMuted = false,
}: FloatingVolumeControlProps) {
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef<number | null>(null);

  const handleMouseEnter = () => {
    if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = window.setTimeout(() => {
      setIsOpen(false);
    }, 800);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    };
  }, []);

  const getVolumeIcon = () => {
    if (isMuted || volume === 0) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
        </svg>
      );
    }
    if (volume < 0.33) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
        </svg>
      );
    }
    if (volume < 0.66) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072M18.364 5.636a9 9 0 010 12.728" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
        </svg>
      );
    }
    return (
      <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072M18.364 5.636a9 9 0 010 12.728" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
      </svg>
    );
  };

  const percentage = Math.round(volume * 100);

  return (
    <div
      className="fixed bottom-24 right-6 z-[100] flex flex-col items-center"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Volume Slider Popup */}
      <div 
        className={`mb-4 transition-all duration-300 transform origin-bottom ${
          isOpen 
            ? 'opacity-100 scale-100 translate-y-0' 
            : 'opacity-0 scale-95 translate-y-4 pointer-events-none'
        }`}
      >
        <div className="bg-[#1a1a2e]/90 backdrop-blur-xl border border-white/10 p-4 rounded-xl shadow-2xl flex flex-col items-center gap-4">
          <span className="text-white font-bold text-sm tabular-nums">
            {percentage}%
          </span>
          <div className="relative h-32 w-8 flex justify-center items-center">
            {/* Custom Vertical Slider using rotation */}
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={volume}
              onChange={(e) => onVolumeChange(parseFloat(e.target.value))}
              className="absolute w-32 h-1.5 accent-purple-500 cursor-pointer appearance-none bg-white/10 rounded-full transition-all hover:bg-white/20 -rotate-90"
              style={{
                outline: 'none',
              }}
            />
          </div>
        </div>
      </div>

      {/* Control Button */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onMuteToggle();
        }}
        className={`p-3 rounded-xl shadow-2xl transition-all duration-300 transform hover:scale-105 active:scale-95 group ${
          isMuted || volume === 0
            ? 'bg-red-500/80 hover:bg-red-500 text-white'
            : 'bg-[#1a1a2e]/90 hover:bg-purple-600/90 text-white border border-white/10 backdrop-blur-xl'
        }`}
      >
        <div className="relative">
          {getVolumeIcon()}
          {!isMuted && volume > 0 && (
            <div className="absolute inset-0 bg-purple-500 blur-lg opacity-20 group-hover:opacity-40 transition-opacity" />
          )}
        </div>
      </button>
    </div>
  );
}
